import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  TimeEntry,
  Project,
  Client,
  Pricelist,
  Task,
  projectsAPI,
  clientsAPI,
  tasksAPI,
  timeEntriesAPI,
  pricelistsAPI,
  complianceAPI,
  tagsAPI,
  API_BASE_URL,
} from '../services/api';
import { TagMultiSelect } from './shared/TagMultiSelect';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

interface SmartTimeEntryFormProps {
  timeEntry?: TimeEntry;
  onSuccess: (entry: TimeEntry) => void;
  onCancel: () => void;
}

interface TimeEntryFormData {
  client_id?: number;
  project_id?: number;
  entry_date: string;
  start_time?: string;
  end_time?: string;
  hours_worked: number;
  description: string;
  is_billable?: boolean;
  billable_rate?: number;
  invoiced?: boolean;
}

interface ComplianceAnalysis {
  sred_compliance: {
    eligible: boolean;
    confidence: number;
    level: string;
    matched_keywords: string[];
    reasoning: string;
  };
  cdae_compliance: {
    eligible: boolean;
    confidence: number;
    level: string;
    matched_keywords: string[];
    reasoning: string;
  };
  suggestions: Array<{
    type: string;
    message: string;
    examples: string[];
  }>;
  questions?: string[];
  recommended_description?: string;
  confidence_score: number;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const SmartTimeEntryForm: React.FC<SmartTimeEntryFormProps> = ({
  timeEntry,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<number | undefined>(
    undefined,
  );
  const [orgTags, setOrgTags] = useState<any[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState<ComplianceAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showEnhancementPrompt, setShowEnhancementPrompt] = useState(false);
  const [currentPricelist, setCurrentPricelist] = useState<Pricelist | null>(
    null,
  );
  const [loadingRates, setLoadingRates] = useState(false);

  // AI Chat state
  const [showAiChat, setShowAiChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [extractedDescription, setExtractedDescription] = useState<
    string | null
  >(null);

  // Extract description from AI response
  const extractDescriptionFromMessage = (content: string): string | null => {
    const tagMatch = content.match(
      /\[DESCRIPTION\]\s*([\s\S]+?)\s*\[\/DESCRIPTION\]/i,
    );
    if (tagMatch) {
      return tagMatch[1].trim();
    }
    return null;
  };

  // Draft a description from the user's GitHub/Jira activity for the chosen
  // project + date, then drop it into the description field for review.
  const [suggestingActivity, setSuggestingActivity] = useState(false);
  const handleSuggestFromActivity = async () => {
    const projectId = getValues('project_id');
    const date = getValues('entry_date');
    if (!projectId) {
      setError(t('timeEntry.selectProjectFirst'));
      return;
    }
    setSuggestingActivity(true);
    setError('');
    try {
      const res = await timeEntriesAPI.getActivitySuggestions({
        project_id: projectId,
        date,
      });
      const draft = res.data?.draft_description as string | undefined;
      const counts = res.data?.source_counts || { github: 0, jira: 0 };
      if (draft && draft.trim()) {
        setValue('description', draft, { shouldValidate: true });
        setInitialDescription(draft);
      } else {
        setError(
          t('timeEntry.noActivityFound', {
            defaultValue: 'No GitHub/Jira activity found for that day.',
          }),
        );
      }
      void counts;
    } catch (e) {
      setError(
        t('timeEntry.activitySuggestFailed', {
          defaultValue: 'Could not load activity suggestions.',
        }),
      );
    } finally {
      setSuggestingActivity(false);
    }
  };

  // Apply extracted description to form
  const handleApplyDescription = (description: string) => {
    setValue('description', description, { shouldValidate: true });
    setInitialDescription(description);
    setExtractedDescription(null);
    setShowAiChat(false);
  };

  const { user } = useAuth();
  const canEditBilling = user?.role === 'manager' || user?.role === 'admin';
  // Handle both project_id directly or project.id from nested object
  const getProjectId = (entry?: TimeEntry) => {
    return entry?.project_id || entry?.project?.id;
  };

  const [selectedProjectId, setSelectedProjectId] = useState<
    number | undefined
  >(getProjectId(timeEntry));
  const [initialDescription, setInitialDescription] = useState<string>(
    timeEntry?.description || '',
  );
  const [initialProjectId, setInitialProjectId] = useState<number | undefined>(
    getProjectId(timeEntry),
  );
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(!timeEntry); // For new entries, mark as loaded immediately

  const formatTimeForInput = (value?: string) => {
    if (!value) return '';

    const hhmmMatch = value.match(/^(\d{2}:\d{2})/);
    if (hhmmMatch) return hhmmMatch[1];

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';

    return format(parsed, 'HH:mm');
  };

  const combineDateAndTime = (date: string, time: string) => {
    if (!date || !time) return undefined;
    return `${date}T${time}:00`;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    reset,
    setValue,
  } = useForm<TimeEntryFormData>({
    defaultValues: {
      entry_date: format(new Date(), 'yyyy-MM-dd'),
      start_time: '',
      end_time: '',
      hours_worked: 8,
      description: '',
      client_id: undefined,
      project_id: undefined,
      is_billable: true,
      billable_rate: 0,
      invoiced: false,
    },
  });

  const watchDescription = watch('description');
  const watchClientId = watch('client_id');
  const watchProjectId = watch('project_id');
  const watchEntryDate = watch('entry_date');
  const watchStartTime = watch('start_time');
  const watchEndTime = watch('end_time');

  useEffect(() => {
    if (!watchStartTime || !watchEndTime || !watchEntryDate) return;

    const start = new Date(`${watchEntryDate}T${watchStartTime}:00`);
    const end = new Date(`${watchEntryDate}T${watchEndTime}:00`);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    if (end <= start) return;

    const durationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    setValue('hours_worked', Number(durationHours.toFixed(2)), {
      shouldValidate: true,
    });
  }, [watchStartTime, watchEndTime, watchEntryDate, setValue]);

  // Get the selected project
  const selectedProject = projects.find(
    (p) => p.id === (watchProjectId || selectedProjectId),
  );

  // Filter projects based on selected client
  const filteredProjects = projects.filter((project) => {
    if (!watchClientId) return true; // Show all projects if no client selected
    return project.client_id === watchClientId;
  });

  // Fetch pricelist rates for the selected project
  const fetchPricelistRates = async (projectId: number, clientId?: number) => {
    if (!user?.role) return;

    setLoadingRates(true);
    try {
      const response = await pricelistsAPI.getAll({
        project_id: projectId,
        client_id: clientId,
        user_id: user.id,
      });

      const pricelists = response.data.pricelists || [];
      if (pricelists.length > 0) {
        // Use the first matching pricelist (most specific)
        const pricelist = pricelists[0];
        setCurrentPricelist(pricelist);

        // Auto-populate the billable rate if not already set
        if (!timeEntry && pricelist.billable_rate) {
          setValue('billable_rate', pricelist.billable_rate);
        }
      } else {
        setCurrentPricelist(null);
      }
    } catch (error) {
      console.error('Failed to load pricelist rates:', error);
      setCurrentPricelist(null);
    } finally {
      setLoadingRates(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsResponse, clientsResponse, tagsResponse] =
          await Promise.all([
            projectsAPI.getAll(),
            clientsAPI.getAll(),
            tagsAPI.getAll(),
          ]);
        setProjects(projectsResponse.data.projects || []);
        setClients(clientsResponse.data.clients || []);
        setOrgTags(tagsResponse.data || []);
      } catch (error) {
        console.error('Failed to load data:', error);
        setError(t('errors.failedToLoad'));
      }
    };

    fetchData();
  }, []);

  // Effect to reset project when client changes
  useEffect(() => {
    const currentProjectId = watchProjectId;
    if (currentProjectId && watchClientId) {
      // Check if current project belongs to selected client
      const currentProject = projects.find((p) => p.id === currentProjectId);
      if (currentProject && currentProject.client_id !== watchClientId) {
        // Reset project if it doesn't belong to selected client
        setValue('project_id', undefined);
        setSelectedProjectId(undefined);
      }
    }
  }, [watchClientId, watchProjectId, projects, setValue]);

  // Effect to fetch pricelist rates when project changes
  useEffect(() => {
    const currentProjectId = watchProjectId || selectedProjectId;
    const project = projects.find((p) => p.id === currentProjectId);

    if (currentProjectId && project) {
      fetchPricelistRates(currentProjectId, project.client?.id);
    } else {
      setCurrentPricelist(null);
    }
  }, [watchProjectId, selectedProjectId, projects, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the selected project's tasks; keep the chosen task only if it still
  // belongs to the project, otherwise clear it.
  useEffect(() => {
    const pid = watchProjectId || selectedProjectId;
    if (!pid) {
      setTasks([]);
      setSelectedTaskId(undefined);
      return;
    }
    let cancelled = false;
    tasksAPI
      .getAll({ project_id: pid })
      .then((res) => {
        if (cancelled) return;
        const list: Task[] = res.data.tasks || [];
        setTasks(list);
        setSelectedTaskId((cur) =>
          cur && list.some((tk) => tk.id === cur) ? cur : undefined,
        );
      })
      .catch(() => {
        if (!cancelled) setTasks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [watchProjectId, selectedProjectId]);

  // Reset form when timeEntry prop changes
  useEffect(() => {
    if (timeEntry) {
      const projectId = getProjectId(timeEntry);

      // Set the selected project state
      setSelectedProjectId(projectId);

      // Set selected tags
      setSelectedTagIds(timeEntry.tag_ids || []);

      // Preselect the entry's task (kept by the task-loading effect if valid)
      setSelectedTaskId(timeEntry.task_id || undefined);

      // Reset form fields
      reset({
        entry_date: timeEntry?.entry_date || format(new Date(), 'yyyy-MM-dd'),
        start_time: formatTimeForInput(timeEntry?.start_time),
        end_time: formatTimeForInput(timeEntry?.end_time),
        hours_worked: timeEntry?.hours_worked || 8,
        description: timeEntry?.description || '',
        client_id: timeEntry?.project?.client?.id || undefined,
        project_id: timeEntry?.project_id || undefined,
        is_billable: timeEntry?.is_billable ?? true,
        billable_rate: timeEntry?.billable_rate || 0,
        invoiced: timeEntry?.invoiced || false,
      });

      // Set project_id in form
      if (projectId) {
        setValue('project_id', projectId, { shouldValidate: true });
      }

      // Load existing AI analysis if available
      if (timeEntry.ai_analysis_data) {
        setAnalysis(timeEntry.ai_analysis_data as ComplianceAnalysis);
      } else {
        setAnalysis(null);
      }

      // Update initial values for change detection
      setInitialDescription(timeEntry.description || '');
      setInitialProjectId(projectId);

      // Mark that initial loading is complete after a short delay
      setTimeout(() => setHasInitiallyLoaded(true), 100);
    }
  }, [timeEntry, reset, setValue]);

  const analyzeCompliance = useCallback(
    async (description: string, projectId?: number) => {
      if (!description || description.length < 10) {
        setAnalysis(null);
        return;
      }

      setAnalyzing(true);
      try {
        const selectedProject = projects.find((p) => p.id === projectId);
        const response = await fetch(
          `${API_BASE_URL}/compliance/analyze_description`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
            body: JSON.stringify({
              description,
              project_id: projectId,
              time_entry_id: timeEntry?.id, // Include time entry ID if editing
              project_context: selectedProject
                ? {
                    sred_eligible: selectedProject.sred_eligible,
                    cdae_eligible: selectedProject.cdae_eligible,
                  }
                : null,
            }),
          },
        );

        if (response.ok) {
          const result = await response.json();
          const analysisData = result.analysis || result;
          setAnalysis(analysisData);

          // Show enhancement prompt if confidence is low
          setShowEnhancementPrompt(analysisData.confidence_score < 60);
        } else {
          console.error(
            'Analysis API failed:',
            response.status,
            response.statusText,
          );
        }
      } catch (error) {
        console.error('Failed to analyze compliance:', error);
      } finally {
        setAnalyzing(false);
      }
    },
    [projects, timeEntry?.id],
  );

  // Debounced analysis effect - only trigger on actual changes
  useEffect(() => {
    // Don't run analysis if we haven't finished initial loading
    if (!hasInitiallyLoaded) {
      return;
    }

    // Check if description or project has actually changed from initial values
    const descriptionChanged = watchDescription !== initialDescription;
    const projectChanged = watchProjectId !== initialProjectId;
    const hasChanges = descriptionChanged || projectChanged;

    if (!hasChanges) {
      return; // No changes detected, skip analysis
    }

    const timer = setTimeout(() => {
      if (watchDescription && watchDescription.length >= 10) {
        analyzeCompliance(watchDescription, watchProjectId);
      } else {
        // Clear analysis if description is too short
        setAnalysis(null);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [
    watchDescription,
    watchProjectId,
    analyzeCompliance,
    hasInitiallyLoaded,
    initialDescription,
    initialProjectId,
  ]);

  const onSubmit = async (data: TimeEntryFormData) => {
    setLoading(true);
    setError('');

    // Validate that project is selected
    if (!data.project_id) {
      setError(t('timeEntry.projectRequired'));
      setLoading(false);
      return;
    }

    // Task is required when the selected project has tasks.
    if (tasks.length > 0 && !selectedTaskId) {
      setError(
        t('timeEntry.taskRequired', {
          defaultValue: 'Please select a task for this project.',
        }),
      );
      setLoading(false);
      return;
    }

    try {
      // Auto-determine SR&ED activity category based on analysis
      let selectedCategoryId = 1; // Default category

      // Use AI analysis to suggest the best category
      if (analysis) {
        if (analysis.sred_compliance.eligible) {
          selectedCategoryId = 2; // Research category
        } else if (analysis.cdae_compliance.eligible) {
          selectedCategoryId = 3; // Development category
        }
      }

      const payload: any = {
        ...data,
        hours_worked: Number(data.hours_worked),
        project_id: data.project_id || 0,
        sred_activity_category_id: selectedCategoryId,
        // Add compliance analysis results if available
        is_sred_eligible: analysis?.sred_compliance.eligible || false,
        is_cdae_eligible: analysis?.cdae_compliance.eligible || false,
        task_id: selectedTaskId ?? null,
        tag_ids: selectedTagIds,
      };

      const hasStart = !!data.start_time;
      const hasEnd = !!data.end_time;

      if (hasStart !== hasEnd) {
        setError(t('timeEntry.bothTimesRequired'));
        setLoading(false);
        return;
      }

      if (hasStart && hasEnd) {
        const start = new Date(`${data.entry_date}T${data.start_time}:00`);
        const end = new Date(`${data.entry_date}T${data.end_time}:00`);

        if (end <= start) {
          setError(t('timeEntry.endAfterStart'));
          setLoading(false);
          return;
        }

        payload.start_time = combineDateAndTime(
          data.entry_date,
          data.start_time!,
        );
        payload.end_time = combineDateAndTime(data.entry_date, data.end_time!);
        payload.hours_worked = Number(
          ((end.getTime() - start.getTime()) / (1000 * 60 * 60)).toFixed(2),
        );
      } else {
        // null (not undefined) so axios keeps the keys and the backend clears stored times
        payload.start_time = null;
        payload.end_time = null;
      }

      let response;
      if (timeEntry?.id) {
        response = await timeEntriesAPI.update(timeEntry.id, payload);
      } else {
        response = await timeEntriesAPI.create(payload);
      }

      onSuccess(response.data.time_entry);
    } catch (error: any) {
      setError(error.response?.data?.error || t('errors.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnhanceDescription = async () => {
    const currentDescription = getValues('description');

    try {
      const response = await fetch(
        `${API_BASE_URL}/compliance/suggest_improvements`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: JSON.stringify({
            description: currentDescription,
            project_id: watchProjectId,
            improvement_focus:
              (analysis?.confidence_score || 0) < 30 ? 'general' : 'specific',
          }),
        },
      );

      if (response.ok) {
        const suggestions = await response.json();
        // Show modal with suggested improvements
        setShowEnhancementPrompt(false);
      }
    } catch (error) {
      console.error('Failed to get enhancement suggestions:', error);
    }
  };

  // Handle AI chat with streaming
  const handleSendChatMessage = async (message?: string) => {
    const messageToSend = message || chatInput.trim();
    if (!messageToSend) return;

    const currentDescription = getValues('description');

    // Add user message to chat
    setChatMessages((prev) => [
      ...prev,
      { role: 'user', content: messageToSend },
    ]);
    setChatInput('');
    setChatLoading(true);
    setStreamingMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/compliance/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          message: messageToSend,
          description: currentDescription,
          project_id: watchProjectId,
          time_entry_id: timeEntry?.id,
          conversation_id: conversationId,
          stream: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullMessage = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.done) {
                  setConversationId(data.conversation_id);
                  setChatMessages((prev) => [
                    ...prev,
                    { role: 'assistant', content: fullMessage },
                  ]);
                  setStreamingMessage('');

                  // Check for extracted description in the response
                  const extracted = extractDescriptionFromMessage(fullMessage);
                  if (extracted) {
                    setExtractedDescription(extracted);
                  }
                } else if (data.error) {
                  console.error('Chat error:', data.error);
                } else if (typeof data === 'string') {
                  fullMessage += data;
                  setStreamingMessage(fullMessage);
                }
              } catch {
                // Skip malformed chunks
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to send chat message:', error);
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t('ai.assistant.errorMessage') },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  // Use recommended description
  const handleUseRecommendedDescription = () => {
    if (analysis?.recommended_description) {
      setValue('description', analysis.recommended_description, {
        shouldValidate: true,
      });
      setInitialDescription(analysis.recommended_description);
    }
  };

  // Handle clicking on a suggested question
  const handleAskQuestion = (question: string) => {
    setShowAiChat(true);
    handleSendChatMessage(question);
  };

  const cardClass = isDark
    ? 'bg-slate-800/50 border border-slate-700/50'
    : 'bg-white border border-slate-200 shadow-md';

  const inputClass = isDark
    ? 'bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:ring-violet-500 focus:border-violet-500'
    : 'bg-white border-gray-300 text-slate-900 focus:ring-blue-500 focus:border-blue-500';

  const labelClass = isDark ? 'text-slate-300' : 'text-gray-700';

  return (
    <div className={`max-w-4xl mx-auto p-6 rounded-2xl ${cardClass}`}>
      <h2
        className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
      >
        {timeEntry ? t('timeEntry.editEntry') : t('timeEntry.newEntry')}
      </h2>

      {error && (
        <div
          className={`mb-4 p-3 rounded-lg ${isDark ? 'bg-red-500/10 border border-red-500/20 text-red-400' : 'bg-red-100 border border-red-400 text-red-700'}`}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Client Selection */}
          <div>
            <label className={`block text-sm font-medium ${labelClass} mb-1`}>
              {t('timeEntry.client')}
            </label>
            <select
              value={watchClientId || ''}
              onChange={(e) => {
                const value =
                  e.target.value === '' ? undefined : Number(e.target.value);
                setValue('client_id', value, { shouldValidate: true });
                // Reset project when client changes
                if (watchProjectId && value) {
                  const currentProject = projects.find(
                    (p) => p.id === watchProjectId,
                  );
                  if (currentProject && currentProject.client_id !== value) {
                    setValue('project_id', undefined);
                    setSelectedProjectId(undefined);
                  }
                }
              }}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
            >
              <option value="">{t('timeEntry.allClients')}</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                  {client.company ? ` (${client.company})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {t('timeEntry.selectClientFilter')}
            </p>
          </div>

          {/* Project Selection */}
          <div>
            <label className={`block text-sm font-medium ${labelClass} mb-1`}>
              {t('timeEntry.project')} *
            </label>
            <select
              value={watchProjectId || selectedProjectId || ''}
              onChange={(e) => {
                const value =
                  e.target.value === '' ? undefined : Number(e.target.value);
                setSelectedProjectId(value);
                setValue('project_id', value, { shouldValidate: true });
              }}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
            >
              <option value="">{t('timeEntry.selectProject')}</option>
              {filteredProjects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                  {project.is_internal ? ` (${t('common.internal')})` : ''}
                </option>
              ))}
            </select>
            {!watchProjectId && (
              <p className="mt-1 text-sm text-red-600">
                {t('timeEntry.projectRequired')}
              </p>
            )}

            {/* Client Information Display */}
            {selectedProject && (
              <div className="mt-2 p-2 bg-gray-50 rounded-md border">
                <div className="text-xs text-gray-600">
                  {selectedProject.is_internal ? (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      {t('timeEntry.internalProject')}
                    </span>
                  ) : selectedProject.client ? (
                    <div>
                      <span className="font-medium">
                        {t('timeEntry.client')}:
                      </span>{' '}
                      {selectedProject.client.name}
                      {selectedProject.client.company && (
                        <span className="text-gray-500">
                          {' '}
                          ({selectedProject.client.company})
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-red-500">
                      {t('timeEntry.noClientAssigned')}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Task Selection — only when the project has tasks (then required) */}
          {tasks.length > 0 && (
            <div>
              <label className={`block text-sm font-medium ${labelClass} mb-1`}>
                {t('timeEntry.task', { defaultValue: 'Task' })} *
              </label>
              <select
                value={selectedTaskId ?? ''}
                onChange={(e) =>
                  setSelectedTaskId(
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
                className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
              >
                <option value="">
                  {t('timeEntry.selectTask', {
                    defaultValue: 'Select a task...',
                  })}
                </option>
                {tasks.map((task) => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
              {!selectedTaskId && (
                <p className="mt-1 text-sm text-red-600">
                  {t('timeEntry.taskRequired', {
                    defaultValue: 'Please select a task for this project.',
                  })}
                </p>
              )}
            </div>
          )}

          {/* Date */}
          <div>
            <label className={`block text-sm font-medium ${labelClass} mb-1`}>
              {t('timeEntry.date')} *
            </label>
            <input
              type="date"
              {...register('entry_date', {
                required: t('timeEntry.dateRequired'),
              })}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
            />
            {errors.entry_date && (
              <p className="mt-1 text-sm text-red-600">
                {errors.entry_date.message}
              </p>
            )}
          </div>

          {/* Hours Worked */}
          <div>
            <label className={`block text-sm font-medium ${labelClass} mb-1`}>
              {t('timeEntry.hours')} *
            </label>
            <input
              type="number"
              step="0.25"
              min="0"
              max="24"
              {...register('hours_worked', {
                required: t('timeEntry.hoursRequired'),
                min: { value: 0.25, message: t('timeEntry.minHours') },
                max: { value: 24, message: t('timeEntry.maxHours') },
              })}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
            />
            {errors.hours_worked && (
              <p className="mt-1 text-sm text-red-600">
                {errors.hours_worked.message}
              </p>
            )}
          </div>

          {/* Start Time */}
          <div>
            <label className={`block text-sm font-medium ${labelClass} mb-1`}>
              {t('timeEntry.startTime')}
            </label>
            <input
              type="time"
              {...register('start_time')}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
            />
          </div>

          {/* End Time */}
          <div>
            <label className={`block text-sm font-medium ${labelClass} mb-1`}>
              {t('timeEntry.endTime')}
            </label>
            <input
              type="time"
              {...register('end_time')}
              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
            />
          </div>
        </div>

        <p
          className={`text-xs -mt-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
        >
          {t('timeEntry.timeRangeHint')}
        </p>

        {/* Smart Description with AI Analysis */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={`block text-sm font-medium ${labelClass}`}>
              {t('timeEntry.whatDidYouWork')} *
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSuggestFromActivity}
                disabled={suggestingActivity}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                title={t('timeEntry.suggestFromActivityHint', {
                  defaultValue:
                    'Draft a description from your GitHub/Jira activity on this date',
                })}
              >
                {suggestingActivity
                  ? t('timeEntry.suggesting', { defaultValue: 'Suggesting…' })
                  : t('timeEntry.suggestFromActivity', {
                      defaultValue: 'Suggest from my activity',
                    })}
              </button>
              {analyzing && (
                <div className="flex items-center text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  {t('compliance.aiAnalyzing')}
                </div>
              )}
            </div>
          </div>

          <textarea
            {...register('description', {
              required: t('timeEntry.descriptionRequired'),
              minLength: {
                value: 10,
                message: t('timeEntry.descriptionMinLength'),
              },
            })}
            rows={4}
            className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
            placeholder={t('timeEntry.descriptionPlaceholder')}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">
              {errors.description.message}
            </p>
          )}
        </div>

        {/* Tags */}
        <div>
          <label className={`block text-sm font-medium ${labelClass} mb-2`}>
            {t('timeEntry.tags')}
          </label>
          <TagMultiSelect
            selectedTagIds={selectedTagIds}
            onChange={(ids) => setSelectedTagIds(ids)}
            orgTags={orgTags}
            isManager={false}
          />
        </div>

        {/* AI Analysis Results */}
        <div>
          <div className="mt-4">
            {analyzing && (
              <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded-lg animate-pulse">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                  <div>
                    <h4 className="text-sm font-semibold text-blue-900">
                      {t('compliance.aiAnalysisInProgress')}
                    </h4>
                    <p className="text-sm text-blue-800 mt-1">
                      {t('compliance.aiAnalyzing')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!analyzing && analysis && (
              <div className="space-y-4 animate-in fade-in duration-500">
                {/* Main Analysis Results */}
                <div
                  className={`rounded-lg p-4 ${isDark ? 'bg-slate-700/50 border border-slate-600' : 'bg-white border border-gray-200 shadow-sm'}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4
                      className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}
                    >
                      {t('compliance.aiAnalysis')}
                      {timeEntry?.ai_analyzed_at && (
                        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {t('compliance.saved')}{' '}
                          {new Date(
                            timeEntry.ai_analyzed_at,
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </h4>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        {analysis.confidence_score}%
                      </div>
                      <div className="text-xs text-gray-500">
                        {t('compliance.confidence')}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* SR&ED Status */}
                    <div
                      className={`p-3 rounded-lg border ${analysis.sred_compliance.eligible ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div
                            className={`w-3 h-3 rounded-full ${analysis.sred_compliance.eligible ? 'bg-green-500' : 'bg-red-500'} mr-2`}
                          ></div>
                          <span className="font-medium text-gray-900">
                            {t('compliance.eligible')}
                          </span>
                        </div>
                        <span
                          className={`text-sm font-semibold ${analysis.sred_compliance.eligible ? 'text-green-800' : 'text-red-800'}`}
                        >
                          {analysis.sred_compliance.eligible ? '✓ Yes' : '✗ No'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {analysis.sred_compliance.reasoning}
                      </p>
                    </div>

                    {/* CDAE Status */}
                    <div
                      className={`p-3 rounded-lg border ${analysis.cdae_compliance.eligible ? 'bg-purple-50 border-purple-200' : 'bg-red-50 border-red-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div
                            className={`w-3 h-3 rounded-full ${analysis.cdae_compliance.eligible ? 'bg-purple-500' : 'bg-red-500'} mr-2`}
                          ></div>
                          <span className="font-medium text-gray-900">
                            {t('compliance.cdaeEligible')}
                          </span>
                        </div>
                        <span
                          className={`text-sm font-semibold ${analysis.cdae_compliance.eligible ? 'text-purple-800' : 'text-red-800'}`}
                        >
                          {analysis.cdae_compliance.eligible ? '✓ Yes' : '✗ No'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {analysis.cdae_compliance.reasoning}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Improvement Suggestions */}
                {analysis.confidence_score < 70 && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center mb-2">
                      <svg
                        className="h-5 w-5 text-yellow-600 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <h5 className="font-medium text-yellow-800">
                        {t('compliance.suggestionsTitle')}
                      </h5>
                    </div>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      {analysis.suggestions?.map((suggestion, index) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{suggestion.message}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* AI Questions */}
                {analysis.questions && analysis.questions.length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center mb-3">
                      <svg
                        className="h-5 w-5 text-blue-600 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <h5 className="font-medium text-blue-800">
                        {t('compliance.aiQuestions')}
                      </h5>
                    </div>
                    <div className="space-y-2">
                      {analysis.questions.map((question, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleAskQuestion(question)}
                          className="w-full text-left p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-colors text-sm text-blue-900"
                        >
                          <span className="mr-2">💬</span>
                          {question}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAiChat(true)}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {t('compliance.openChat')}
                    </button>
                  </div>
                )}

                {/* Recommended Description */}
                {analysis.recommended_description && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <svg
                          className="h-5 w-5 text-green-600 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <h5 className="font-medium text-green-800">
                          {t('compliance.recommendedDescription')}
                        </h5>
                      </div>
                      <button
                        type="button"
                        onClick={handleUseRecommendedDescription}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                      >
                        {t('compliance.useThisDescription')}
                      </button>
                    </div>
                    <p className="text-sm text-green-900 bg-white p-3 rounded border border-green-200">
                      {analysis.recommended_description}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Enhancement Prompt */}
            {!analyzing &&
              showEnhancementPrompt &&
              analysis &&
              analysis.confidence_score < 60 && (
                <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-purple-900 flex items-center">
                        {t('compliance.enhancementAvailable')}
                      </h4>
                      <p className="text-sm text-purple-800 mt-1">
                        {t('compliance.enhancementDescription')}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleEnhanceDescription}
                        className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        {t('compliance.enhanceWithAI')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowEnhancementPrompt(false)}
                        className="px-3 py-2 border border-purple-300 text-purple-700 text-sm rounded-lg hover:bg-purple-100 transition-colors"
                      >
                        {t('compliance.skip')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Billing Information */}
        {canEditBilling && (
          <div
            className={`p-4 rounded-xl ${isDark ? 'bg-slate-700/30 border border-slate-600' : 'bg-gray-50'}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}
              >
                {t('billing.title')}
              </h3>
              {loadingRates && (
                <div
                  className={`flex items-center text-sm ${isDark ? 'text-violet-400' : 'text-blue-600'}`}
                >
                  <div
                    className={`animate-spin rounded-full h-4 w-4 border-b-2 ${isDark ? 'border-violet-400' : 'border-blue-600'} mr-2`}
                  ></div>
                  {t('timeEntry.loadingRates')}
                </div>
              )}
            </div>

            {/* Current Pricelist Info */}
            {currentPricelist && (
              <div
                className={`mb-4 p-3 rounded-lg border ${isDark ? 'bg-violet-500/10 border-violet-500/30' : 'bg-blue-50 border-blue-200'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4
                      className={`text-sm font-medium ${isDark ? 'text-violet-300' : 'text-blue-900'}`}
                    >
                      {t('billing.activePricelist')}: {currentPricelist.name}
                    </h4>
                    <p
                      className={`text-xs ${isDark ? 'text-violet-400' : 'text-blue-700'}`}
                    >
                      {currentPricelist.scope_description}
                    </p>
                  </div>
                  <div className="text-right">
                    <div
                      className={`text-sm font-medium ${isDark ? 'text-violet-300' : 'text-blue-900'}`}
                    >
                      {currentPricelist.formatted_billable_rate}
                    </div>
                    <div
                      className={`text-xs ${isDark ? 'text-violet-400' : 'text-blue-700'}`}
                    >
                      {t('billing.billableRate')}
                    </div>
                  </div>
                </div>
                {currentPricelist.description && (
                  <p
                    className={`text-xs mt-2 ${isDark ? 'text-violet-400' : 'text-blue-600'}`}
                  >
                    {currentPricelist.description}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Billable Toggle */}
              <div>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    {...register('is_billable')}
                    className={`rounded ${isDark ? 'border-slate-500 bg-slate-600 text-emerald-500 focus:ring-emerald-500' : 'border-gray-300 text-green-600 focus:ring-green-500'}`}
                  />
                  <span
                    className={`ml-2 text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                  >
                    {t('billing.billable')}
                  </span>
                </label>
                <p
                  className={`mt-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                >
                  {t('billing.billableDescription')}
                </p>
              </div>

              {/* Billable Rate */}
              <div>
                <label
                  className={`block text-sm font-medium ${labelClass} mb-1`}
                >
                  {t('billing.billableRate')}
                  {currentPricelist && (
                    <span
                      className={`text-xs ml-1 ${isDark ? 'text-violet-400' : 'text-blue-600'}`}
                    >
                      ({currentPricelist.currency})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('billable_rate')}
                    className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 ${inputClass}`}
                    placeholder={
                      currentPricelist
                        ? currentPricelist.billable_rate.toString()
                        : '0.00'
                    }
                  />
                  {currentPricelist && (
                    <button
                      type="button"
                      onClick={() =>
                        setValue(
                          'billable_rate',
                          currentPricelist.billable_rate,
                        )
                      }
                      className={`absolute right-2 top-2 text-xs ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-blue-600 hover:text-blue-800'}`}
                      title={t('billing.useRate')}
                    >
                      {t('billing.useRate')}
                    </button>
                  )}
                </div>
                {currentPricelist && (
                  <p
                    className={`mt-1 text-xs ${isDark ? 'text-violet-400' : 'text-blue-600'}`}
                  >
                    {t('billing.pricelistRate')}:{' '}
                    {currentPricelist.formatted_billable_rate}
                  </p>
                )}
              </div>

              {/* Invoiced Toggle - managers/admins only */}
              {canEditBilling && (
                <div>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      {...register('invoiced')}
                      className={`rounded ${isDark ? 'border-slate-500 bg-slate-600 text-violet-500 focus:ring-violet-500' : 'border-gray-300 text-blue-600 focus:ring-blue-500'}`}
                    />
                    <span
                      className={`ml-2 text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                    >
                      {t('billing.invoiced')}
                    </span>
                  </label>
                  <p
                    className={`mt-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                  >
                    {t('billing.invoicedDescription')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div
          className={`flex flex-col-reverse md:flex-row justify-end space-y-4 md:space-y-0 md:space-x-4 gap-4 md:gap-0 pt-6 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}
        >
          <button
            type="button"
            onClick={onCancel}
            className={`px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 ${
              isDark
                ? 'border-slate-600 text-slate-300 hover:bg-slate-700/50 focus:ring-slate-500'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
            }`}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? t('timeEntry.saving')
              : timeEntry
                ? t('timeEntry.updateEntry')
                : t('timeEntry.createEntry')}
          </button>
        </div>
      </form>

      {/* AI Chat Panel */}
      {showAiChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col ${isDark ? 'bg-slate-800' : 'bg-white'}`}
          >
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-xl">
              <div className="flex items-center">
                <svg
                  className="w-6 h-6 text-white mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                <h3 className="text-lg font-semibold text-white">
                  {t('compliance.aiAssistant')}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAiChat(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px]">
              {chatMessages.length === 0 && !streamingMessage && (
                <div className="text-center text-gray-500 py-8">
                  <svg
                    className="w-12 h-12 mx-auto mb-4 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  <p>{t('compliance.chatWelcome')}</p>
                  <p className="text-sm mt-2">{t('compliance.chatHint')}</p>
                </div>
              )}

              {chatMessages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-2 ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}

              {/* Streaming message */}
              {streamingMessage && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg px-4 py-2 bg-gray-100 text-gray-900">
                    <p className="text-sm whitespace-pre-wrap">
                      {streamingMessage}
                    </p>
                    <span className="inline-block w-2 h-4 bg-blue-600 animate-pulse ml-1"></span>
                  </div>
                </div>
              )}

              {chatLoading && !streamingMessage && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg px-4 py-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0.1s' }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0.2s' }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Extracted Description Banner */}
            {extractedDescription && (
              <div className="p-4 bg-green-50 border-t border-green-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <svg
                        className="w-5 h-5 text-green-600 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="font-medium text-green-800">
                        {t('compliance.descriptionReady')}
                      </span>
                    </div>
                    <p className="text-sm text-green-900 bg-white p-2 rounded border border-green-200 mb-2">
                      {extractedDescription}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => handleApplyDescription(extractedDescription)}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    {t('compliance.applyDescription')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setExtractedDescription(null)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Chat Input */}
            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === 'Enter' && !chatLoading && handleSendChatMessage()
                  }
                  placeholder={t('compliance.chatPlaceholder')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={chatLoading}
                />
                <button
                  type="button"
                  onClick={() => handleSendChatMessage()}
                  disabled={chatLoading || !chatInput.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartTimeEntryForm;
